/// <amd-module name="Repos/Discussion/Editor/Integrations/MonacoDiscussionIntegration" />
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { IDiffMonacoDiscussionIntegration, IDiffMonacoDiscussionWidget } from "Repos/Discussion/Common/DiscussionContracts";
import { getAddCommentGutterRegistration } from "../Widgets/AddCommentGutterWidget";
import { EmptyDiffDiscussionWidget, getAddCommentRegistration } from "../Widgets/AddCommentWidget";
import { DiscussionZoneWidgetRegistration, IDiscussionZoneWidgetRegistrationProps } from "../Widgets/DiscussionZoneWidget";
import { BaseDiscussionIntegration, IDiscussionIntegrationProps } from "./BaseDiscussionIntegration";
import { getLinkWidgetRegistration } from "VSS/Features/MonacoWidgets/Registration";
import { IEditorSelection } from "Repos/Common/Contracts/Editor";
import { IFilesHubViewService } from "Repos/Views/FilesHub/FilesHubViewService";
import { Uri } from "VSS/Platform/Util/Url";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IMonacoDiscussionIntegrationProps extends IDiscussionIntegrationProps {
    getCodeSelectionLinkUrl: (selection: IEditorSelection, editorType?: number, diffOrientation?: DiffViewerOrientation) => string
}

export class MonacoDiscussionIntegration
    extends BaseDiscussionIntegration<IDiffMonacoDiscussionWidget, IMonacoDiscussionIntegrationProps>
    implements IDiffMonacoDiscussionIntegration {
    private pendingToRevealThreadId: number | undefined;

    constructor(props: IMonacoDiscussionIntegrationProps) {
        super(props);

        const { canAddComments, diffOrientation } = props;
        diffOrientation!.subscribe(this.changeOrientation);

        if (canAddComments) {
            // Initialize widgets with gutter and overlay widgets to add a comment
            this.widgets.push(
                getAddCommentGutterRegistration({
                    addToOriginalDiff: true,
                    diffOrientation: diffOrientation!.value,
                    onAddDiscussion: this.onStartThread
                })
            );
            this.widgets.push(
                getAddCommentGutterRegistration({
                    addToOriginalDiff: false,
                    diffOrientation: diffOrientation!.value,
                    onAddDiscussion: this.onStartThread
                })
            );
            this.widgets.push(getAddCommentRegistration({ addToOriginalDiff: true, onAddDiscussion: this.onStartThread }));
            this.widgets.push(getAddCommentRegistration({ addToOriginalDiff: false, onAddDiscussion: this.onStartThread }));
        }

        if (isFeatureFlagEnabled(props.pageContext, "Git.PullRequest.MonacoLinkToCodeSelection")) {
            const filePath = props.filePath.split(".").pop();
            const linkWidgetOriginal = getLinkWidgetRegistration(this.props.getCodeSelectionLinkUrl, this.props.pageContext, filePath!, true);
            const linkWidgetModified = getLinkWidgetRegistration(this.props.getCodeSelectionLinkUrl, this.props.pageContext, filePath!, false);
            
            this.widgets.push({
                ...EmptyDiffDiscussionWidget,
                ...linkWidgetOriginal,
                addToOriginalDiff: true
            });
    
            this.widgets.push({
                ...EmptyDiffDiscussionWidget,
                ...linkWidgetModified,
                addToOriginalDiff: false
            });
        }

        this.initialize();
    }

    private getCodeSelectionLinkUrl = (selection: IEditorSelection, editorType?: number): string => {
        const filesService = this.props.pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
        const url = new Uri(filesService.getItemUrl(filesService.getSelectedItem().value.path, { absoluteUrl: true }));
        url.addQueryParams({
            line: selection.startLineNumber,
            lineEnd: selection.endLineNumber,
            lineStartColumn: selection.startColumn,
            lineEndColumn: selection.endColumn,
            type: editorType,
            orientation: this.props.diffOrientation!.value,
            lineStyle: "plain",
            _a: "files"
        });
        return url.absoluteUri;
    };

    public changeOrientation = (diffOrientation: DiffViewerOrientation): void => {
        // Update discussion widgets to adapt new orientation
        for (const widget of this.widgets.value) {
            if (widget instanceof DiscussionZoneWidgetRegistration) {
                widget.changeOrientation(diffOrientation);
            }
        }
    };

    public dispose() {
        this.props.diffOrientation!.unsubscribe(this.changeOrientation);

        super.dispose();
    }

    public revealThreadId(threadId: number) {
        for (const widget of this.widgets.value) {
            if (widget instanceof DiscussionZoneWidgetRegistration && widget.containsThread(threadId)) {
                widget.revealWidget();
                return;
            }
        }

        // If no widget found, we're likely loading, so save the Id to reveal it when it's created
        this.pendingToRevealThreadId = threadId;
    }

    protected isMyWidgetInstance(widget: IDiffMonacoDiscussionWidget): boolean {
        return widget instanceof DiscussionZoneWidgetRegistration;
    }

    protected createWidget(widgetProps: IDiscussionZoneWidgetRegistrationProps): DiscussionZoneWidgetRegistration {
        return new DiscussionZoneWidgetRegistration({
            ...widgetProps,
            contentClassName: "repos-monaco-discussion-host",
            diffOrientation: this.props.diffOrientation!.value,
            scrollToThreadId: this.pendingToRevealThreadId
        });
    }
}
